import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Input, Stack } from "@wato/ui";
import { isApiError } from "../../api/client";
import { useApi, useErrorMessage } from "../../api/context";
import { useAuth } from "../../auth/AuthProvider";
import { SETUP_ROUTE } from "../../auth/guards";
import { AuthLayout, safeNextPath } from "../../components/common";

/**
 * "Sign in with WhatsApp" (WP-564, FR-AUTH-011): phone step -> code step. Same post-login
 * redirect as the email/Google flows; the server never creates an account from a phone.
 */
export function PhoneOtpLogin() {
  const { t, locale } = useI18n();
  const api = useApi();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const errorMessage = useErrorMessage();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = safeNextPath(params.get("next")) ?? "/";

  function failure(caught: unknown): string {
    if (isApiError(caught)) {
      if (caught.code === "AUTH_RATE_LIMITED") {
        const seconds = Number(caught.details.retryAfterSeconds ?? 60);
        return t("dashboard.auth.phoneOtp.cooldown", { seconds });
      }
      if (caught.code === "AUTH_TOKEN_INVALID") return t("dashboard.auth.phoneOtp.invalid");
      if (caught.code === "AUTH_TOKEN_USED") return t("dashboard.auth.phoneOtp.used");
      if (caught.code === "AUTH_TOKEN_EXPIRED") return t("dashboard.auth.phoneOtp.expired");
    }
    return errorMessage(caught);
  }

  async function send(event?: FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.requestPhoneOtp({ phone: phone.trim(), language: locale });
      setStep("code");
    } catch (caught) {
      setError(failure(caught));
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.verifyPhoneOtp({ phone: phone.trim(), code: code.trim() });
      if (result.status === "2fa_required") {
        await navigate(`/login/2fa?next=${encodeURIComponent(next)}`, {
          state: { challengeToken: result.challengeToken },
        });
        return;
      }
      await refresh();
      await navigate(result.status === "2fa_setup_required" ? SETUP_ROUTE : next, {
        replace: true,
      });
    } catch (caught) {
      setError(failure(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("dashboard.auth.phoneOtp.title")}
      subtitle={t("dashboard.auth.phoneOtp.subtitle")}
      footer={
        <Link className="font-semibold text-on-action-ghost underline" to="/login">
          {t("dashboard.auth.phoneOtp.useEmail")}
        </Link>
      }
    >
      <form onSubmit={step === "phone" ? send : verify} noValidate>
        <Stack gap={4}>
          {error && <Alert tone="error">{error}</Alert>}
          {step === "code" && !error && (
            <Alert tone="success">{t("dashboard.auth.phoneOtp.sent")}</Alert>
          )}
          <Input
            type="tel"
            dir="ltr"
            autoComplete="tel"
            required
            label={t("dashboard.auth.phoneOtp.phone")}
            value={phone}
            disabled={step === "code"}
            onChange={(e) => setPhone(e.target.value)}
          />
          {step === "code" && (
            <Input
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              label={t("dashboard.auth.phoneOtp.code")}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          )}
          <Button type="submit" fullWidth loading={busy}>
            {step === "phone"
              ? t("dashboard.auth.phoneOtp.send")
              : t("dashboard.auth.phoneOtp.verify")}
          </Button>
          {step === "code" && (
            <Button variant="secondary" fullWidth disabled={busy} onClick={() => void send()}>
              {t("dashboard.auth.phoneOtp.resend")}
            </Button>
          )}
        </Stack>
      </form>
    </AuthLayout>
  );
}
